// Storage container detail — lists the documents inside a single
// container. Reached from the storage-containers admin list via the
// 'Open' row action or by clicking the name (#98).
//
// Reuses GET /api/v1/storage-containers/:id/documents (already
// implemented; returns a lightweight row shape, not the full
// DocumentListItem). Click a row → /documents/:id for full detail.
//
// Drop-files extension (#116): drag-and-drop on this page uploads each
// file via /documents/upload then REPLACES the doc's container via
// PUT /documents/:id/container.
//
// CTSK-45 hotfix (0.1.77, 2026-07-27): previously used POST
// /storage-containers/:id/documents which ADDS to the container
// without removing prior assignments — the doc ended up in BOTH
// Inbox (from default-intake catch-all) AND the drag-target
// container. dr.the0's original design ("I'm not sure I want
// something to belong to two containers") maps to REPLACE
// semantics, so we now use the PUT route which does DELETE-all-
// then-INSERT. Only affects this drag-drop path; the
// POST /storage-containers/:id/documents route is preserved for
// callers that legitimately want multi-container add semantics.

import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import type { StorageContainer } from '@captain-tasko/shared';
import { apiList, apiSingle, apiUpload } from '../lib/api.js';
import { SkeletonRows } from '../components/Skeleton.js';
import { EmptyState } from '../components/EmptyState.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { toast } from '../components/Toast.js';
import { formatBytes, iconLabelForMime } from '../lib/format.js';
import { MimeIcon } from '../components/MimeIcon.js';
import { useTableSort } from '../lib/sort.js';
import { SortableTh } from '../components/SortableTh.js';
import { AuditHistoryDrawer } from '../components/AuditHistoryDrawer.js';

interface ContainerDoc {
  documentId: string;
  title: string;
  sizeBytes: number;
  updatedAt: string;
  mimeType: string;
  status: string;
}

export function StorageContainerDetail({
  id,
  onNavigate,
}: {
  id: string;
  onNavigate: (path: string) => void;
}) {
  const qc = useQueryClient();
  // CTSK-389 — History side-drawer (resource_audit timeline for this
  // container). Same "sibling button, not a tab" pattern as
  // DocumentDetail's History affordance — this page has no tab strip
  // to slot a tab into.
  const [historyOpen, setHistoryOpen] = useState(false);

  // Two queries: the container row (for header info) + its document
  // list. Both are scoped to active org by the backend.
  const containerQ = useQuery({
    queryKey: ['storage-container', id],
    queryFn: () => apiSingle<StorageContainer>(`/storage-containers/${id}`),
  });

  const docsQ = useQuery({
    queryKey: ['storage-container-docs', id],
    queryFn: () => apiList<ContainerDoc>(`/storage-containers/${id}/documents`),
  });

  const docs = docsQ.data?.data ?? [];

  // #116 — drop-files-into-container state. Mirrors the WorkItemDetail
  // pattern: sequential per-file upload + add-to-container, with a
  // status strip the user can dismiss.
  const [dragOver, setDragOver] = useState(false);
  type AttachStatus = 'uploading' | 'attaching' | 'done' | 'error';
  interface AttachState {
    id: number;
    filename: string;
    status: AttachStatus;
    error?: string;
    documentId?: string;
  }
  const [uploads, setUploads] = useState<AttachState[]>([]);
  const uploadIdRef = useRef(0);

  /**
   * Upload each dropped/picked file then add it to THIS container.
   * Sequential to avoid the same race that bit the bulk uploader
   * (#114) — one file at a time, awaits in between, no setState
   * inside a synchronous while loop.
   */
  async function uploadAndAddFiles(files: FileList | File[]) {
    const list = Array.from(files);
    if (list.length === 0) return;
    for (const file of list) {
      const localId = ++uploadIdRef.current;
      setUploads((prev) => [...prev, { id: localId, filename: file.name, status: 'uploading' }]);
      try {
        const uploadRes = await apiUpload<{ document: { id: string; title: string } }>(
          '/documents/upload',
          file,
        );
        const documentId = uploadRes.document.id;
        setUploads((prev) =>
          prev.map((u) => (u.id === localId ? { ...u, status: 'attaching', documentId } : u)),
        );

        // CTSK-45 hotfix (0.1.77): use PUT /documents/:id/container
        // (REPLACE semantics — deletes prior containers then inserts
        // this one) instead of POST /storage-containers/:id/documents
        // (ADD semantics — leaves default-intake's Inbox assignment
        // in place, producing the multi-container bug the ticket
        // reported).
        await apiSingle<{ ok: boolean; containerId: string | null }>(
          `/documents/${documentId}/container`,
          { method: 'PUT', body: { containerId: id } },
        );

        setUploads((prev) =>
          prev.map((u) => (u.id === localId ? { ...u, status: 'done' } : u)),
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed';
        setUploads((prev) =>
          prev.map((u) => (u.id === localId ? { ...u, status: 'error', error: message } : u)),
        );
        toast.error(`${file.name}: ${message}`);
      }
    }
    // Refresh the docs list + container counts so the user sees the
    // new files without manually reloading.
    qc.invalidateQueries({ queryKey: ['storage-container-docs', id] });
    qc.invalidateQueries({ queryKey: ['storage-container', id] });
    qc.invalidateQueries({ queryKey: ['documents'] });
  }

  function dismissUpload(localId: number) {
    setUploads((prev) => prev.filter((u) => u.id !== localId));
  }

  const { sorted, sort, setSort } = useTableSort(docs, {
    title: (d: ContainerDoc) => d.title.toLowerCase(),
    sizeBytes: (d) => d.sizeBytes,
    updatedAt: (d) => d.updatedAt,
    status: (d) => d.status,
    mimeType: (d) => d.mimeType,
  }, { key: 'updatedAt', dir: 'desc' });

  if (containerQ.isLoading) return <SkeletonRows rows={4} />;
  if (containerQ.isError || !containerQ.data) {
    return (
      <EmptyState
        title="Container not found"
        body="The container may have been deleted or belongs to another organization."
        action={<button className="btn" onClick={() => onNavigate('/storage')}>← Back to containers</button>}
      />
    );
  }

  const c = containerQ.data;

  return (
    <div
      // #116 — page-wide drop zone. Drop anywhere on the page and the
      // file lands in THIS container. Dashed border highlight on
      // drag-over so the affordance is visible.
      onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
      onDragOver={(e) => { e.preventDefault(); }}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const files = e.dataTransfer.files;
        if (files && files.length > 0) uploadAndAddFiles(files);
      }}
      style={{
        outline: dragOver ? '2px dashed var(--accent-primary)' : 'none',
        outlineOffset: dragOver ? -8 : 0,
        borderRadius: 'var(--radius-md)',
        transition: 'outline-color 100ms',
        minHeight: '60vh',
      }}
    >
      <div className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => onNavigate('/storage')}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                fontSize: 13,
              }}
              title="Back to all containers"
            >
              ← Storage
            </button>
            <h1 className="page-title" style={{ margin: 0 }}>{c.name}</h1>
            {c.isDefault && (
              <span style={{
                fontSize: 10,
                padding: '2px 6px',
                borderRadius: 8,
                background: 'var(--surface-sunken)',
                color: 'var(--text-muted)',
              }}>default</span>
            )}
          </div>
          <div className="page-subtitle">
            <code style={{ fontFamily: 'var(--font-mono)' }}>{c.slug}</code>
            <span style={{ marginLeft: 12 }}>{docs.length} document{docs.length === 1 ? '' : 's'}</span>
            {c.totalBytes > 0 && (
              <span style={{ marginLeft: 12 }}>{formatBytes(c.totalBytes)}</span>
            )}
            {c.description && (
              <div style={{ marginTop: 4, color: 'var(--text-muted)' }}>{c.description}</div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {/* #116 — click-to-pick fallback. Same handler as the drop
              zone; files flow through upload + add-to-container. */}
          <label className="btn btn--primary" style={{ cursor: 'pointer' }}>
            <input
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                const files = e.target.files;
                if (files && files.length > 0) uploadAndAddFiles(files);
                e.target.value = '';
              }}
            />
            + Add files
          </label>
          <button type="button" className="btn" onClick={() => setHistoryOpen(true)}>
            History
          </button>
        </div>
      </div>
      {historyOpen && (
        <AuditHistoryDrawer resourceType="storage_container" resourceId={id} onClose={() => setHistoryOpen(false)} />
      )}

      {/* #116 — inline upload status strip. Renders only while there
          are uploads in flight or recent failures. Each row dismissable. */}
      {uploads.length > 0 && (
        <ul
          style={{
            listStyle: 'none',
            margin: '0 0 16px',
            padding: '8px 12px',
            background: 'var(--surface-canvas)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 6,
            fontSize: 12,
          }}
        >
          {uploads.map((u) => (
            <li
              key={u.id}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}
              title={u.error ?? u.status}
            >
              <span
                style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background:
                    u.status === 'done' ? 'var(--success-primary, #3C8E7A)' :
                    u.status === 'error' ? 'var(--danger-primary, #B3261E)' :
                    'var(--accent-primary)',
                  flex: '0 0 8px',
                }}
              />
              <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {u.filename}
              </span>
              <span style={{ color: 'var(--text-muted)' }}>
                {u.status === 'uploading' && 'uploading…'}
                {u.status === 'attaching' && 'adding to container…'}
                {u.status === 'done' && (
                  u.documentId
                    ? <a href={`/documents/${u.documentId}`} onClick={(e) => { e.preventDefault(); onNavigate(`/documents/${u.documentId}`); }} style={{ color: 'var(--accent-primary)' }}>view</a>
                    : 'done'
                )}
                {u.status === 'error' && (u.error ?? 'failed')}
              </span>
              <button
                type="button"
                onClick={() => dismissUpload(u.id)}
                className="btn btn--ghost"
                style={{ padding: '0 6px', fontSize: 12 }}
                aria-label={`Dismiss ${u.filename}`}
              >×</button>
            </li>
          ))}
        </ul>
      )}

      {docsQ.isLoading && <SkeletonRows rows={6} />}

      {docsQ.data && docs.length === 0 && (
        <EmptyState
          title="No documents in this container yet"
          body="Documents land here either by upload + file workflow routing, or by tagging-rule containerToAssign actions."
        />
      )}

      {docs.length > 0 && (
        <table className="grid grid--spacious">
          <thead>
            <tr>
              <SortableTh sortKey="title" sort={sort} onSortChange={setSort} sortable>
                Title
              </SortableTh>
              {/* HTML data type — open in browser directly. Narrow,
                  unsorted, between Title and Status. Cells render
                  empty for non-HTML mime types so the column is
                  visually quiet on rows where it doesn't apply. */}
              <th style={{ width: 32 }} aria-label="Open in browser"></th>
              <SortableTh sortKey="status" sort={sort} onSortChange={setSort} sortable style={{ width: 120 }}>
                Status
              </SortableTh>
              <SortableTh sortKey="mimeType" sort={sort} onSortChange={setSort} sortable style={{ width: 160 }}>
                Type
              </SortableTh>
              <SortableTh sortKey="sizeBytes" sort={sort} onSortChange={setSort} sortable style={{ width: 100, textAlign: 'right' }}>
                Size
              </SortableTh>
              <SortableTh sortKey="updatedAt" sort={sort} onSortChange={setSort} sortable style={{ width: 180 }}>
                Updated
              </SortableTh>
            </tr>
          </thead>
          <tbody>
            {sorted.map((d) => (
              <tr
                key={d.documentId}
                style={{ cursor: 'pointer' }}
                onClick={() => onNavigate(`/documents/${d.documentId}`)}
              >
                <td>
                  <span
                    title={iconLabelForMime(d.mimeType)}
                    aria-label={iconLabelForMime(d.mimeType)}
                    style={{ marginRight: 8, color: 'var(--text-muted)', display: 'inline-flex', verticalAlign: 'text-bottom' }}
                  >
                    <MimeIcon mime={d.mimeType} size={16} />
                  </span>
                  <a
                    href={`/documents/${d.documentId}`}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onNavigate(`/documents/${d.documentId}`); }}
                    style={{ color: 'var(--accent-primary)', fontWeight: 500, textDecoration: 'none' }}
                  >
                    {d.title}
                  </a>
                </td>
                <td style={{ textAlign: 'center' }}>
                  {d.mimeType.startsWith('text/html') && (
                    <a
                      href={`/api/v1/documents/${d.documentId}/content`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => { e.stopPropagation(); }}
                      title={`Open "${d.title}" in a new tab`}
                      aria-label={`Open "${d.title}" in a new tab`}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        color: 'var(--text-muted)',
                        padding: 2,
                      }}
                    >
                      <ExternalLink size={14} aria-hidden />
                    </a>
                  )}
                </td>
                <td><StatusBadge status={d.status as never} variant="document" /></td>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>
                  {d.mimeType}
                </td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {formatBytes(d.sizeBytes)}
                </td>
                <td>{new Date(d.updatedAt).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
